import { useCallback } from 'react';
import { useToast } from '../components/Toast';
import { getToken } from '../utils/api';
import confetti from 'canvas-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Major milestone badges that trigger confetti
const MILESTONE_BADGES = [
  'first_debt_destroyed',  // First debt paid off
  'debt_free',             // All debts paid
  'super_saver',           // $1,000 saved
  'goal_getter',           // First savings goal reached
  'emergency_fund_complete', // Emergency fund fully funded
  'financial_master',      // Earn 10 badges
];

// Gold tier badges trigger special confetti
const GOLD_TIER_BADGES = ['debt_free', 'super_saver', 'emergency_fund_complete', 'financial_master', 'challenge_champion'];

/**
 * Trigger milestone confetti celebration
 */
const triggerConfetti = (isGoldTier = false) => {
  if (isGoldTier) {
    // Epic gold confetti for gold-tier achievements
    const end = Date.now() + 3 * 1000;
    const colors = ['#FFD700', '#FFC107', '#FFEB3B', '#FFE082', '#FFB300'];

    (function frame() {
      confetti({
        particleCount: 5,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors
      });
      confetti({
        particleCount: 5,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    }());
  } else {
    // Standard celebration confetti
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6']
    });
  }
};

/**
 * Hook to check for and celebrate newly earned badges
 * Call after actions that might earn badges (paying debt, saving money, etc.)
 */
export function useBadgeChecker() {
  const { toast } = useToast();

  const checkBadges = useCallback(async () => {
    try {
      const token = getToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API}/badges/check`, {
        method: 'POST',
        credentials: 'include',
        headers,
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Show celebration toasts for newly earned badges
        if (data.newly_earned && data.newly_earned.length > 0) {
          // Check if any milestone badges were earned
          const earnedMilestones = data.newly_earned.filter(badge => 
            MILESTONE_BADGES.includes(badge.id)
          );
          
          // Check if any gold tier badges were earned
          const earnedGoldTier = data.newly_earned.filter(badge => 
            GOLD_TIER_BADGES.includes(badge.id)
          );
          
          // Trigger confetti for milestones
          if (earnedMilestones.length > 0) {
            const isGold = earnedGoldTier.length > 0;
            triggerConfetti(isGold);
          }
          
          data.newly_earned.forEach((badge, index) => {
            // Stagger the toasts slightly for multiple badges
            setTimeout(() => {
              toast.badge(`${badge.name}`, badge.emoji);
            }, index * 600);
          });
          
          return data.newly_earned;
        }
      }
    } catch (error) {
      console.error('Error checking badges:', error);
    }
    return [];
  }, [toast]);

  return { checkBadges, triggerConfetti };
}

export default useBadgeChecker;
